import { Send } from './icons';
import React from 'react';
import './chat.css';
import { Spin, message, Modal } from 'antd';
import { ajax } from "../utils/request";
import { useTranslation } from "react-i18next";
import { getToken } from "../utils/storage";
import { marked } from 'marked';
import CONSTRUCTION_PDF_DATA from './10002.40 Construction Daily_pdf_dates.json';
import SPECS_MAPPING from './specs_mapping.json';

export default function Chat({ setPdfLink, onQuerySubmit, resetSignal, currentChatId, currentChatMessages, ...props }) {
  const textAreaRef = React.useRef(null);
  const chatContentRef = React.useRef(null);

  const [chatList, setChatList] = React.useState([]);
  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);
  const [isAnswering, setIsAnswering] = React.useState(false);
  const [question, setQuestion] = React.useState('');
  const [messageApi, contextHolder] = message.useMessage();

  // --- CORPUS STATE ---
  // allCorpora: full list from /admin/corpora
  const [allCorpora, setAllCorpora] = React.useState([]);
  // Derived filter values built from allCorpora
  const [jurisdictions, setJurisdictions] = React.useState([]);
  const [selectedJurisdiction, setSelectedJurisdiction] = React.useState('');
  const [disciplines, setDisciplines] = React.useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = React.useState('');
  const [categories, setCategories] = React.useState([]);
  const [selectedCategory, setSelectedCategory] = React.useState('');
  // The resolved corpus object (from allCorpora) based on current dropdown state
  const [selectedCorpus, setSelectedCorpus] = React.useState(null);

  // Info about the currently open chat (populated when loading an existing chat)
  const [currentChatInfo, setCurrentChatInfo] = React.useState({ corpus: null });

  const { confirm } = Modal;
  const { t } = useTranslation();
  const [count, setCount] = React.useState(1);
  const [userData, setUserData] = React.useState(null);
  const [libraryDocs, setLibraryDocs] = React.useState([]);

  // ---------------------------------------------------------------------------
  // Load corpora and library docs on mount
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    ajax.get('/admin/corpora')
      .then(res => {
        if (res.data.code === 0 && Array.isArray(res.data.data)) {
          const data = res.data.data;
          setAllCorpora(data);
          // Build unique jurisdiction list
          const juris = [...new Set(data.map(c => c.jurisdiction))].sort();
          setJurisdictions(juris);
        }
      })
      .catch(err => console.error("Failed to load corpora:", err));

    ajax.get('/admin/library/documents')
      .then(res => {
        if (res.data.code === 0) {
          setLibraryDocs(res.data.data);
        }
      })
      .catch(err => console.error("Failed to load library docs:", err));
  }, []);

  // ---------------------------------------------------------------------------
  // Cascade: jurisdiction → disciplines
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    if (!selectedJurisdiction) {
      setDisciplines([]);
      setSelectedDiscipline('');
      setCategories([]);
      setSelectedCategory('');
      setSelectedCorpus(null);
      return;
    }
    const filtered = allCorpora.filter(c => c.jurisdiction === selectedJurisdiction);
    const disc = [...new Set(filtered.map(c => c.discipline))].sort();
    setDisciplines(disc);
    setSelectedDiscipline('');
    setCategories([]);
    setSelectedCategory('');
    setSelectedCorpus(null);
  }, [selectedJurisdiction, allCorpora]);

  // ---------------------------------------------------------------------------
  // Cascade: discipline → categories (may be null)
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    if (!selectedDiscipline) {
      setCategories([]);
      setSelectedCategory('');
      setSelectedCorpus(null);
      return;
    }
    const filtered = allCorpora.filter(
      c => c.jurisdiction === selectedJurisdiction && c.discipline === selectedDiscipline
    );
    const cats = [...new Set(filtered.map(c => c.category).filter(Boolean))].sort();
    setCategories(cats);
    setSelectedCategory('');

    // If there are no categories, resolve the corpus immediately
    if (cats.length === 0) {
      setSelectedCorpus(filtered[0] || null);
    } else {
      setSelectedCorpus(null);
    }
  }, [selectedDiscipline, selectedJurisdiction, allCorpora]);

  // ---------------------------------------------------------------------------
  // Cascade: category → corpus
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    if (categories.length === 0) return; // already resolved above
    if (!selectedCategory) {
      setSelectedCorpus(null);
      return;
    }
    const match = allCorpora.find(
      c =>
        c.jurisdiction === selectedJurisdiction &&
        c.discipline === selectedDiscipline &&
        c.category === selectedCategory
    );
    setSelectedCorpus(match || null);
  }, [selectedCategory, selectedDiscipline, selectedJurisdiction, allCorpora, categories]);

  // ---------------------------------------------------------------------------
  // Permission check helpers — driven entirely by corpus.corpus_permission from DB
  // corpus_permission is a comma-separated string of allowed emails, or null/empty = public
  // ---------------------------------------------------------------------------
  const isCorpusAllowed = (corpus) => {
    if (!corpus) return false;
    const perm = corpus.corpus_permission;
    // null, empty string, or whitespace-only → public corpus, always allowed
    if (!perm || perm.trim() === "") return true;
    // If userData hasn't loaded yet, show the discipline optimistically
    if (!userData) return true;
    // corpus_permission may be stored as a JSON array string ["a@b.com","c@d.com"]
    // or as a plain comma-separated string "a@b.com,c@d.com"
    let allowedEmails = [];
    const trimmed = perm.trim();
    if (trimmed.startsWith("[")) {
      try {
        allowedEmails = JSON.parse(trimmed).map(e => e.trim().toLowerCase());
      } catch {
        allowedEmails = trimmed.replace(/[\[\]"]/g, "").split(",").map(e => e.trim().toLowerCase());
      }
    } else {
      allowedEmails = trimmed.split(",").map(e => e.trim().toLowerCase());
    }
    const userEmail = (userData.email || "").trim().toLowerCase();
    return allowedEmails.includes(userEmail);
  };

  // Show a discipline if AT LEAST ONE corpus under it is allowed for this user.
  // (A discipline may have multiple corpora with different categories/permissions.)
  const allowedDisciplines = disciplines.filter(disc => {
    const corporaForDisc = allCorpora.filter(
      c => c.jurisdiction === selectedJurisdiction && c.discipline === disc
    );
    return corporaForDisc.length > 0 && corporaForDisc.some(c => isCorpusAllowed(c));
  });

  // Show a category if its specific corpus is allowed.
  const allowedCategories = categories.filter(cat => {
    const corpusForCat = allCorpora.find(
      c =>
        c.jurisdiction === selectedJurisdiction &&
        c.discipline === selectedDiscipline &&
        c.category === cat
    );
    return isCorpusAllowed(corpusForCat);
  });

  // ---------------------------------------------------------------------------
  // Construction Daily helpers
  // ---------------------------------------------------------------------------
  const MONTH_MAP = {
    january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
    july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
    jan: '01', feb: '02', mar: '03', apr: '04', jun: '06', jul: '07', aug: '08',
    sep: '09', oct: '10', nov: '11', dec: '12',
  };

  const getUrlFromGs = (gsPath) => {
    const path = gsPath.replace('gs://', '');
    const encodedPath = path.split('/').map(s => encodeURIComponent(s)).join('/');
    return `https://storage.googleapis.com/${encodedPath}?t=${Date.now()}#page=1`;
  };

  const linkifyConstructionDates = (text) => {
    if (!text) return text;
    const dateRegex = /(?:(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+(\d{1,2})(?:st|nd|rd|th)?[\s,]+(?:\d{2})?(\d{2}))|(\b(?:\d{4}|\d{2})[\/\.-]\d{1,2}[\/\.-](?:\d{4}|\d{2})\b)/gi;

    return text.replace(dateRegex, (match, monthName, dayNum, yearNum, numericDate) => {
      let key = "";
      if (monthName) {
        const month = MONTH_MAP[monthName.toLowerCase()];
        const day = dayNum.padStart(2, '0');
        key = `${yearNum}.${month}.${day}`;
      } else if (numericDate) {
        const parts = numericDate.split(/[\/\.-]/);
        let y, m, d;
        if (parts[0].length === 4 || parseInt(parts[0]) > 12) {
          y = parts[0].slice(-2); m = parts[1].padStart(2, '0'); d = parts[2].padStart(2, '0');
        } else {
          m = parts[0].padStart(2, '0'); d = parts[1].padStart(2, '0'); y = parts[2].slice(-2);
        }
        key = `${y}.${m}.${d}`;
      }
      if (CONSTRUCTION_PDF_DATA[key]) {
        const finalUrl = getUrlFromGs(CONSTRUCTION_PDF_DATA[key]);
        return `<span class="construction-date-link" style="color: #1677ff; font-weight: 600; text-decoration: underline; cursor: pointer;" data-url="${finalUrl}">${match}</span>`;
      }
      return match;
    });
  };

  const linkifySpecs = (text) => {
    if (!text) return text;
    
    /**
     * This regex looks for:
     * 1. (REDWOOD CITY_|DALY CITY_)? -> Optional city prefix
     * 2. (\d{4,5}) -> 4 or 5 digit spec number
     * 3. (?![0-9]) -> Ensures we don't match a substring of a longer number (e.g., 0273 in 02731)
     */
    const regex = /(REDWOOD CITY_|DALY CITY_)?(\d{4,5})(?![0-9])/gi;

    return text.replace(regex, (match, prefix, num) => {
      // Normalize to uppercase to ensure matching with JSON keys
      const upperMatch = match.toUpperCase();
      
      // 1. Priority: Look for the exact full match (handles "REDWOOD CITY_02720" or "DALY CITY_02720")
      if (SPECS_MAPPING[upperMatch]) {
        const finalUrl = getUrlFromGs(SPECS_MAPPING[upperMatch]);
        return `<span class="specs-link" style="color: #1677ff; font-weight: 600; text-decoration: underline; cursor: pointer;" data-url="${finalUrl}">${match}</span>`;
      }
      
      // 2. Fallback: Look for just the number (handles "REDWOOD CITY_01350" -> uses "01350" mapping)
      if (SPECS_MAPPING[num]) {
        const finalUrl = getUrlFromGs(SPECS_MAPPING[num]);
        return `<span class="specs-link" style="color: #1677ff; font-weight: 600; text-decoration: underline; cursor: pointer;" data-url="${finalUrl}">${match}</span>`;
      }

      // If no key is found in specs_mapping.json, return the text as is
      return match;
    });
  };

  // ---------------------------------------------------------------------------
  // PDF redirection from AI citation
  // ---------------------------------------------------------------------------
  const handlePdfRedirection = (answerText) => {
    if (!answerText || !libraryDocs || libraryDocs.length === 0) return;
    const clean = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
    const lines = answerText.split('\n');
    for (let line of lines) {
      if (line.toLowerCase().includes('source:')) {
        const matchedDoc = libraryDocs.find(doc => {
          const dbTitle = clean(doc.title);
          const aiLine = clean(line);
          return aiLine.includes(dbTitle) || dbTitle.includes(aiLine);
        });
        const pageMatch = line.match(/Page\s+([0-9]+)/i);
        const pageNumber = pageMatch ? pageMatch[1] : 1;
        if (matchedDoc) {
          const baseUrl = "https://storage.googleapis.com/";
          const path = matchedDoc.gcs_link.replace('gs://', '');
          const encodedPath = path.split('/').map(s => encodeURIComponent(s)).join('/');
          const finalUrl = `${baseUrl}${encodedPath}?t=${Date.now()}#page=${pageNumber}`;
          if (setPdfLink) setPdfLink(finalUrl);
          return;
        }
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Message rendering
  // ---------------------------------------------------------------------------
  const isConstructionDaily = () => {
    if (currentChatId) {
      return currentChatInfo.corpus?.discipline === "10002.40 Construction Daily";
    }
    return selectedCorpus?.discipline === "10002.40 Construction Daily";
  };

  const isSpecs = () => {
    if (currentChatId) {
      return currentChatInfo.corpus?.discipline === "Specs";
    }
    return selectedCorpus?.discipline === "Specs";
  };

  const getRenderedMessage = (messageContent, role) => {
    let html = marked.parse(messageContent);
    if (role === 'ai') {
      if (isConstructionDaily()) {
        html = linkifyConstructionDates(html);
      } else if (isSpecs()) {
        html = linkifySpecs(html);
      }
    }
    return html;
  };

  // ---------------------------------------------------------------------------
  // Demo scripts
  // ---------------------------------------------------------------------------
  const demoScripts = {
    "1": "What are the sight distances to consider for a design speed of 65 mph on a conventional highway?",
    "2": "What are the maximum spacing requirements for manholes in the sewer system?",
    "3": "What is the City's policy regarding the Level of Service (LOS) for intersection operations?",
    "4": "We are overseeing a project that uses disinfected tertiary recycled water for landscape irrigation near a school. What are the specific watering window and access requirements?",
    "5": "What is the minimum static pressure requirement for the design of potable water mains?",
    "6": "What is the minimum cover required for storm drain and water mains relative to the finished grade?",
    "7": "What is the minimum slope required for horizontal drainage pipes that are 4 inches in diameter?",
  };

  // ---------------------------------------------------------------------------
  // Chat history / reset
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    if (currentChatMessages && Array.isArray(currentChatMessages.messages)) {
      const formattedMessages = currentChatMessages.messages.map(msg => ({
        role: msg.role === 'user' ? 'user' : 'ai',
        content: msg.content,
      }));
      setChatList(formattedMessages);
      props.setShowOverlay(false);
      // corpus may be null for legacy chats — store whatever is present
      setCurrentChatInfo({ corpus: currentChatMessages.corpus ?? null });
    } else {
      setChatList([]);
      props.setShowOverlay(true);
      setSelectedJurisdiction('');
      setSelectedDiscipline('');
      setSelectedCategory('');
      setSelectedCorpus(null);
      setCurrentChatInfo({ corpus: null });
    }
  }, [currentChatMessages, currentChatId, resetSignal]);

  // ---------------------------------------------------------------------------
  // User data
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    getToken().then((token) => {
      if (token) {
        ajax.get(`/user/me`)
          .then(res => setUserData(res.data.data))
          .catch(error => {
            if (error.response && error.response.status === 403) showConfirm();
          });
      } else {
        showConfirm();
      }
    });
  }, [count]);

  // Scroll to bottom
  React.useEffect(() => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTo({ top: chatContentRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [chatList]);

  // Construction daily date click
  React.useEffect(() => {
    const handleDateClick = (e) => {
      if (e.target.classList.contains('construction-date-link') || e.target.classList.contains('specs-link')) {
        const url = e.target.getAttribute('data-url');
        if (setPdfLink) setPdfLink(url);
      }
    };
    document.addEventListener('click', handleDateClick);
    return () => document.removeEventListener('click', handleDateClick);
  }, [setPdfLink]);

  const showConfirm = () => {
    confirm({
      title: t("book.tipTitle"),
      content: t("book.tipContent"),
      onOk() { window.location.href = "/"; },
      onCancel() { window.location.href = "/"; },
    });
  };

  // ---------------------------------------------------------------------------
  // Text input
  // ---------------------------------------------------------------------------
  const handleTextChange = (e) => {
    setQuestion(e.target.value);
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight + "px";
    }
  };

  const handleGhostTyping = (key) => {
    const textToType = demoScripts[key];
    if (!textToType) return;
    let index = 0;
    let currentText = '';
    setQuestion('');
    const typingInterval = setInterval(() => {
      currentText += textToType.charAt(index);
      setQuestion(currentText);
      index++;
      if (textAreaRef.current) {
        textAreaRef.current.style.height = "auto";
        textAreaRef.current.style.height = textAreaRef.current.scrollHeight + "px";
      }
      if (index >= textToType.length) clearInterval(typingInterval);
    }, 55);
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey && e.shiftKey && e.code.startsWith('Digit')) {
      const digit = e.code.replace('Digit', '');
      if (demoScripts[digit]) {
        e.preventDefault();
        e.stopPropagation();
        handleGhostTyping(digit);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !isAnswering) {
      e.preventDefault();
      sendMessage();
    }
  };

  const newMessage = (role, content) => {
    setChatList((prev) => [...prev, { role, content }]);
  };

  // ---------------------------------------------------------------------------
  // Send / API
  // ---------------------------------------------------------------------------
  const isSelectionComplete = () => {
    if (!selectedCorpus) return false;
    // Permission check
    return isCorpusAllowed(selectedCorpus);
  };

  const sendMessage = () => {
    if (question.trim() === '') return;

    if (!currentChatId) {
      if (!isSelectionComplete()) {
        messageApi.warning('Please select a valid corpus (Jurisdiction → Discipline → Category if applicable).');
        return;
      }
    }

    if (props.showOverlay && onQuerySubmit) {
      onQuerySubmit();
    }

    newMessage('user', question);
    fetchResponse(question, currentChatId, selectedCorpus ? selectedCorpus.id : null);
    setQuestion('');
  };

  const fetchResponse = async (q, chatId, corpusId) => {
    setIsAnswering(true);
    try {
      const payload = { question: q, chat_id: chatId || undefined };
      if (!chatId && corpusId != null) {
        payload.corpus_id = corpusId;
      }

      const res = await ajax.post('/chat', payload);
      if (res.data.code === 0) {
        const data = res.data.data;
        const responseText = data.answer || data.response;

        newMessage('ai', responseText);
        if (onQuerySubmit && data.chat_id) onQuerySubmit(data.chat_id);

        if (!isConstructionDaily()) handlePdfRedirection(responseText);
      }
    } catch (err) {
      messageApi.error('Failed to get response');
    } finally {
      setIsAnswering(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Header label for the active corpus
  // ---------------------------------------------------------------------------
  const getCorpusLabel = (corpus) => {
    if (!corpus) return null;
    const parts = [corpus.jurisdiction, corpus.discipline];
    if (corpus.category) parts.push(corpus.category);
    return parts.join(' — ');
  };

  const activeChatCorpusLabel = currentChatId
    ? (currentChatInfo.corpus ? getCorpusLabel(currentChatInfo.corpus) : "Legacy chat")
    : null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="chat-wrapper w-full max-w-full flex flex-col" style={{ height: windowHeight }}>
      {contextHolder}

      {/* Active corpus header */}
      {activeChatCorpusLabel && (
        <div className="chat-header text-center py-2 px-4 bg-gray-100 border-b border-gray-200">
          <p className="text-sm text-gray-700">
            Current Chat: <span className="font-semibold">{activeChatCorpusLabel}</span>
          </p>
        </div>
      )}

      <div
        className="chat-content flex-1 overflow-y-auto"
        ref={chatContentRef}
        style={{ padding: props.showOverlay ? '20px 0' : '20px' }}
      >
        <div className="max-w-[800px] mx-auto w-full px-4">

          {/* WELCOME BOX */}
          {chatList.length === 0 && props.showOverlay && (
            <div className="chat-message-item welcome">
              <div className="chat-message" style={{ padding: '20px 30px', background: '#fff', borderRadius: '12px', maxWidth: '720px', margin: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>

                <div className="flex justify-center items-center gap-4 mb-4">
                  <img src="/images/civichat.png" alt="CiviBOT" style={{ height: '32px' }} />
                  <h1 style={{ fontSize: '24px', margin: 0 }}>
                    Welcome to <span style={{ color: '#2b65d9' }}>CiviChat</span>
                  </h1>
                  <img src="/images/civichat.png" alt="CiviBOT" style={{ height: '32px' }} />
                </div>

                <p style={{ color: '#555', marginBottom: '16px', textAlign: 'center' }}>
                  Your AI Assistant for Engineering Standards &amp; Regulations
                </p>

                <h2 style={{ fontSize: '18px', marginBottom: '16px', textAlign: 'left' }}>📌 How to Use CiviChat Effectively:</h2>

                <div style={{ textAlign: 'left', fontSize: '15px', lineHeight: '1.7', marginBottom: '30px' }}>
                  <p>🔍 <strong>Use Filters –</strong> Select the jurisdiction and discipline relevant to your question.</p>
                  <p>🎯 <strong>Be Specific –</strong> Ask clear and detailed questions for the most accurate responses.</p>
                  <p>📚 <strong>Mention Relevant Standards –</strong> Include known codes or documents to help CiviChat target its answers.</p>
                  <p>❓ <strong>One Question at a Time –</strong> Keep queries focused for clear and concise answers.</p>
                  <p>🚫 <strong>Avoid Vague Questions –</strong> Specificity improves quality and accuracy.</p>
                  <p>📝 <strong>Provide Context –</strong> Add relevant project details that may influence the answer.</p>
                </div>

                {/* CORPUS SELECTION */}
                <div className="flex flex-col gap-4 border-t pt-6">
                  <div className="flex flex-col md:flex-row gap-4">

                    {/* Jurisdiction */}
                    <div className="flex-1 flex flex-col gap-4">
                      <div className="text-left">
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Jurisdiction</label>
                        <select
                          value={selectedJurisdiction}
                          onChange={(e) => setSelectedJurisdiction(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1677ff] focus:border-transparent bg-white text-gray-700 cursor-pointer"
                          disabled={isAnswering || !!currentChatId}
                        >
                          <option value="" disabled>Select Jurisdiction</option>
                          {jurisdictions.map(j => <option key={j} value={j}>{j}</option>)}
                        </select>
                      </div>

                      {/* Discipline */}
                      {selectedJurisdiction && (
                        <div className="text-left">
                          <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Discipline</label>
                          <select
                            value={selectedDiscipline}
                            onChange={(e) => setSelectedDiscipline(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1677ff] focus:border-transparent bg-white text-gray-700 cursor-pointer"
                            disabled={isAnswering || !!currentChatId}
                          >
                            <option value="" disabled>Select Discipline</option>
                            {allowedDisciplines.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Category (conditional) */}
                    {selectedDiscipline && allowedCategories.length > 0 && (
                      <div className="flex-1 text-left">
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Category</label>
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1677ff] focus:border-transparent bg-white text-gray-700 cursor-pointer"
                          disabled={isAnswering || !!currentChatId}
                        >
                          <option value="" disabled>Select Category</option>
                          {allowedCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Selected corpus confirmation */}
                  {selectedCorpus && (
                    <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                      ✓ Corpus selected: <strong>{getCorpusLabel(selectedCorpus)}</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* MESSAGE LIST */}
          <div className="message-list-container">
            {chatList.map((item, index) => (
              <div key={index} className={`chat-message-item ${item.role === 'user' ? 'human' : 'ai'}`}>
                <div className="chat-message">
                  {item.role === 'ai' ? (
                    <div className="markdown-content" dangerouslySetInnerHTML={{ __html: getRenderedMessage(item.content, item.role) }} />
                  ) : (
                    <div className="user-text">{item.content}</div>
                  )}
                </div>
              </div>
            ))}
            {isAnswering && (
              <div className="chat-message-item ai">
                <div className="chat-message"><Spin size="small" /></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* INPUT AREA */}
      <div className={`chat-input-wrapper px-4 ${props.showOverlay ? "mt-6 mb-10 flex flex-col items-center" : "mt-auto border-t py-4 bg-white"}`}>
        {props.showOverlay && <div className="text-black text-[18px] font-semibold mb-2 text-center">How can I help you?</div>}
        <div className={`send-container w-full max-w-[720px] ${props.showOverlay ? "" : "mx-auto"}`} onKeyDown={handleKeyDown}>
          <textarea
            placeholder={(!isSelectionComplete() && !currentChatId) ? "Select criteria first..." : "Ask any question..."}
            className="message-input"
            rows={1}
            ref={textAreaRef}
            value={question}
            onChange={handleTextChange}
          />
          <button
            className="message-send"
            style={{
              background: (isAnswering || (!isSelectionComplete() && !currentChatId)) ? 'rgb(187 189 193)' : '#1677ff',
            }}
            onClick={sendMessage}
            disabled={isAnswering || (!isSelectionComplete() && !currentChatId)}
          >
            <span className="flex flex-col items-center text-center text-[14px]">
              <Send size={14} color="#fff" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}